import * as React from 'react';

import { hasFeature } from 'root/api/admin/features';
import { ErrorMessageContext } from 'root/apps/PurchaseOrder/ShipmentHistory/contexts/ErrorMessage';
import { getApiError } from 'root/apps/PurchaseOrder/ShipmentHistory/utils/api-error-codes';

import type {
  APIError,
  APIResponse,
  ShipmentItemResponse,
  ShipmentOverview,
  ShippingStatus,
} from 'root/apps/PurchaseOrder/ShipmentHistory/types';

interface OverviewPayload {
  orderId: number | string;
  shipmentId: number | string;
  shipmentItemId?: number | string;
  payload?: Partial<ShipmentPayload & ShipmentItemPayload>;
  callback?: () => void;
}

export interface ShipmentPayload {
  shipmentPackingRefNum: string;
  paymentDueDate: string;
  receptionDate: string;
  shippingCost: number;
  shippingVendorCost: number;
  status: ShippingStatus;
}

export interface ShipmentItemPayload {
  qtyReceived: number;
}

export interface Return {
  isLoading: boolean;
  isInitialLoading: boolean;
  savingKeys: null | string[];
  isDisabled: boolean;
  itemIdSaving: number | string | null;
  data: ShipmentOverview | undefined;
  setData: React.Dispatch<React.SetStateAction<ShipmentOverview | undefined>>;
  getOverviewData: (payload: OverviewPayload) => void;
  saveOverviewData: (payload: OverviewPayload) => void;
  saveItemData: (payload: OverviewPayload) => Promise<ShipmentItemResponse | null>;
  hasPurchaseOrderLandedCostsSpreadCosts: boolean;
  hasPurchaseOrderLandedCostsManualDiscountAdjustment: boolean;
}

export const OverviewDataContext = React.createContext<Return>({} as Return);

interface Props {
  data?: ShipmentOverview;
}

const Provider: React.FC<Props> = ({ data: pData, children }) => {
  const { setError } = React.useContext(ErrorMessageContext);
  const [data, setData] = React.useState<ShipmentOverview | undefined>(pData);
  const [isLoading, setIsLoading] = React.useState(!pData);
  const [isInitialLoading, setIsInitialLoading] = React.useState(!pData);
  const [hasPurchaseOrderLandedCostsSpreadCosts, setHasPurchaseOrderLandedCostsSpreadCosts] =
    React.useState(false);
  const [
    hasPurchaseOrderLandedCostsManualDiscountAdjustment,
    setHasPurchaseOrderLandedCostsManualDiscountAdjustment,
  ] = React.useState(false);
  const [savingKeys, setSavingKeys] = React.useState<string[] | null>(null);
  const [itemIdSaving, setItemIdSaving] = React.useState<number | string | null>(null);
  const [isDisabled, setIsDisabled] = React.useState(false);

  const fetchHasFeature = async () => {
    if (await hasFeature('PurchaseOrderLandedCostsSpreadCosts')) {
      setHasPurchaseOrderLandedCostsSpreadCosts(true);
    }
    if (await hasFeature('PurchaseOrderLandedCostsManualDiscountAdjustment')) {
      setHasPurchaseOrderLandedCostsManualDiscountAdjustment(true);
    }
  };

  React.useEffect(() => {
    fetchHasFeature();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (data?.status) {
      setIsDisabled(data.status === 'finished');
    }
  }, [data]);

  const getOverviewData = async ({ orderId, shipmentId }: OverviewPayload) => {
    setIsLoading(true);
    try {
      const res = await fetch(`/admin/order/${orderId}/shipment/${shipmentId}/overview`);
      if (res.status !== 200) {
        throw new Error(getApiError('GENERIC_GET').title);
      }
      const { data }: { data: ShipmentOverview } = await res.json();

      setData(data);
    } catch (err) {
      setError(getApiError('GENERIC_GET'));
    }
    setIsLoading(false);
    setIsInitialLoading(false);
  };

  const saveOverviewData = async ({ orderId, shipmentId, payload, callback }: OverviewPayload) => {
    setSavingKeys(Object.keys(payload || {}));

    window.showNotifier('working');

    try {
      const res = (await fetch(`/admin/order/${orderId}/shipment/${shipmentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })) as APIResponse;

      if (![200, 204].includes(res.status)) {
        const { title, message } = getApiError(res.errorCode, 'PATCH');
        throw new Error(title || message);
      }

      const { data }: { data: ShipmentOverview } = await res.json();

      setData(data);
      window.clearViewTabs();
      callback?.();
      window.flashNotifier('success', 'Saved Changes', 3);
    } catch (err) {
      let thrownError = { message: err.message };
      if ((err as APIError).errorCode) {
        thrownError = getApiError((err as APIError).errorCode, 'PATCH');
      }
      setError(thrownError);
    }

    window.hideNotifier('working');
    setSavingKeys(null);
  };

  const saveItemData = async ({
    orderId,
    shipmentId,
    shipmentItemId,
    payload,
  }: OverviewPayload): Promise<ShipmentItemResponse | null> => {
    let savedData: ShipmentItemResponse;

    if (!shipmentItemId) return null;

    setError(null);
    window.showNotifier('working');
    setItemIdSaving(shipmentItemId);

    try {
      const res = (await fetch(
        `/admin/order/${orderId}/shipment/${shipmentId}/item/${shipmentItemId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      )) as APIResponse;

      if (![200, 204].includes(res.status)) {
        const { title, message } = getApiError(res.errorCode, 'PATCH');
        throw new Error(title || message);
      }

      getOverviewData({ orderId, shipmentId });
      window.clearViewTabs();
      window.flashNotifier('success', 'Saved Changes', 3);

      ({ data: savedData } = await res.json());

      const {
        stockStatus: { insufficientStock, statusAfterManualAdjustment },
      } = savedData;

      if (insufficientStock) {
        window.warnUser(`Item quantity updated. ${statusAfterManualAdjustment}`, 15);
      }
    } catch (err) {
      setError(getApiError((err as APIError).errorCode, 'PATCH'));
    }

    setItemIdSaving(null);
    window.hideNotifier('working');

    // @ts-ignore
    return savedData;
  };

  return (
    <OverviewDataContext.Provider
      value={{
        isLoading,
        isInitialLoading,
        savingKeys,
        isDisabled,
        itemIdSaving,
        data,
        setData,
        getOverviewData,
        saveOverviewData,
        saveItemData,
        hasPurchaseOrderLandedCostsSpreadCosts,
        hasPurchaseOrderLandedCostsManualDiscountAdjustment,
      }}
    >
      {children}
    </OverviewDataContext.Provider>
  );
};

export default Provider;
